import React, { useEffect, useMemo, useRef, useState, forwardRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import PortalModal from 'components/Shared/PortalModal'
import { capitalizeString, localizeDateObj } from 'utils/helpers'
import { SecondaryButton } from 'components/Shared/Button'

const CustomInput = forwardRef(({ value, onChange, suffix, classNames = '', ...props }, ref) => {
  const spanRef = useRef(null)
  const [inputWidth, setInputWidth] = useState(1)

  const updateWidth = () => {
    if (spanRef.current) {
      const width = spanRef.current.offsetWidth
      setInputWidth(width + 2) // padding
    }
  }

  useEffect(() => {
    updateWidth()
  }, [value])

  // focus the input when the component mounts
  useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus()
    }
  }, [ref])

  return (
    <div className={cn('d-inline-flex', 'align-items-center', 'position-relative', classNames)}>
      <span
        ref={spanRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          font: 'inherit',
        }}
      >
        {value}
      </span>
      <input
        {...props}
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: `${inputWidth}px`,
        }}
        maxLength={80} // 100 total allowed, 20 for suffix timestamp
      />
      <span className="suffix" style={{ font: 'inherit' }}>
        {suffix}
      </span>
    </div>
  )
})
CustomInput.displayName = 'CustomInput'

CustomInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  suffix: PropTypes.string.isRequired,
  classNames: PropTypes.string,
}

function ExportModalContent({ isOpen, onClose, serviceName, exportName, onExport, loading, ref }) {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const timestamp = useMemo(
    () => localizeDateObj(new Date(), 'de', 'DD.MM.YYYY.-HH:mm:ss'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  )
  const [containerName, setContainerName] = useState(exportName)

  const _onClose = useCallback(() => {
    setContainerName(exportName)
    onClose()
  }, [exportName, onClose])

  useEffect(() => {
    // when exportName changes, reset the container name
    if (!isOpen && exportName !== containerName) {
      setContainerName(exportName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportName, isOpen])

  console.log({serviceName})

  return (
    <PortalModal
      isOpen={isOpen}
      onClickOutside={_onClose}
      wrapperClassName="integrations_export__modal-wrapper"
      className="integrations_export__modal w-md-60"
      title={t('Integrations.export.title', { serviceName: capitalizeString(serviceName) })}
    >
      <div className="integrations_export__modal" ref={ref}>
        <p className="mt-3">
          {t(`Integrations.export.${serviceName === 'mailchimp' ? 'descriptionTag' : 'description'}`, { serviceName: capitalizeString(serviceName) })}
        </p>
        <div className="integrations_export__modal__name__container">
          <CustomInput
            classNames="integrations_export__modal__name"
            value={containerName}
            onChange={setContainerName}
            suffix={` @ ${timestamp}`}
            className="integrations_export__modal__name__input"
            ref={inputRef}
          />
          <div className="integrations_export__modal__actions">
            <SecondaryButton
              color="orange"
              text={t('common.send')}
              classNames="integrations_export__modal__export-button"
              onClick={() => onExport(`${containerName} @ ${timestamp}`)}
              fullWidth={false}
              disabled={loading || !containerName}
              size="small"
            />
          </div>
        </div>
      </div>
    </PortalModal>
  )
}

ExportModalContent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serviceName: PropTypes.string.isRequired,
  exportName: PropTypes.string.isRequired,
  onExport: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  ref: PropTypes.object,
}

const ExportModal = forwardRef(
  ({ isOpen, onClose, serviceName, exportName, onExport, loading }, ref) => {
    return (
      <ExportModalContent
        ref={ref}
        isOpen={isOpen}
        onClose={onClose}
        serviceName={serviceName}
        exportName={exportName}
        onExport={onExport}
        loading={loading}
      />
    )
  },
)

ExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  exportName: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  onExport: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
}

ExportModal.displayName = 'ExportModal'

export default ExportModal
